import { Navigate } from 'react-router-dom';
import { Can } from 'context';
import { useEffect, useState } from 'react';
import { getFinancialReport, exportFinancialReport } from 'services/financial-report';
import MDTypography from 'components/MDTypography';
import { Autocomplete, Card, Grid, TextField } from '@mui/material';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/DashboardNavbar';
import dayjs from 'dayjs';
import { DateTimePicker } from '@mui/x-date-pickers';
import { pickersLayoutClasses } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import Currency from 'enums/currency.enum';
import MDButton from 'components/MDButton';

function FinancialReport() {
  const [filters, setFilters] = useState([]);
  const [data, setData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const response = await getFinancialReport(filters);
      setData(response.data || []);
    };
    fetchData();
  }, [filters]);

  const handleExport = async (id) => {
    const filter = id ? filters?.find((f) => f.id === id) : undefined;

    const exportData = {};
    if (id) {
      exportData.id = id;
    }
    if (filter?.startDate) {
      exportData.startDate = filter.startDate;
    }
    if (filter?.endDate) {
      exportData.endDate = filter.endDate;
    }
    if (filter?.currency) {
      exportData.currency = filter.currency;
    }

    const response = await exportFinancialReport(exportData);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'financial_report.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const updateFilter = (clientId, patch) => {
    setFilters((prevFilters) => {
      const existing = prevFilters.find((f) => f.id === clientId);

      if (existing) {
        return prevFilters.map((f) => {
          if (f.id !== clientId) return f;

          const updated = { ...f };
          if (patch.startDate !== undefined) {
            updated.startDate = patch.startDate;
          }
          if (patch.endDate !== undefined) {
            updated.endDate = patch.endDate;
          }
          if (patch.currency !== undefined) {
            if (patch.currency === null || patch.currency === 0) {
              delete updated.currency;
            } else {
              updated.currency = patch.currency;
            }
          }
          return updated;
        });
      }

      const obj = { id: clientId };
      if (patch.startDate != null) {
        obj.startDate = patch.startDate;
      }
      if (patch.endDate != null) {
        obj.endDate = patch.endDate;
      }
      if (patch.currency != null && patch.currency !== 0) {
        obj.currency = patch.currency;
      }

      return [...prevFilters, obj];
    });
  };

  const handleFromChange = (date, clientId) => {
    if (!date || !date.isValid()) {
      updateFilter(clientId, { startDate: null });
      return;
    }

    const isoDate = date.toISOString();

    setFilters((prevFilters) => {
      const existing = prevFilters.find((f) => f.id === clientId);
      const existingEnd = existing?.endDate;

      // swap if "from" > "to"
      const shouldSwap = existingEnd && dayjs(isoDate).isAfter(dayjs(existingEnd));
      const patch = shouldSwap ? { startDate: existingEnd, endDate: isoDate } : { startDate: isoDate };

      if (existing) {
        return prevFilters.map((f) => (f.id === clientId ? { ...f, ...patch } : f));
      }
      return [...prevFilters, { id: clientId, ...patch }];
    });
  };

  const handleToChange = (date, clientId) => {
    if (!date || !date.isValid()) {
      updateFilter(clientId, { endDate: null });
      return;
    }

    const isoDate = date.toISOString();

    setFilters((prevFilters) => {
      const existing = prevFilters.find((f) => f.id === clientId);
      const existingStart = existing?.startDate;

      // swap if "to" < "from"
      const shouldSwap = existingStart && dayjs(isoDate).isBefore(dayjs(existingStart));
      const patch = shouldSwap ? { startDate: isoDate, endDate: existingStart } : { endDate: isoDate };

      if (existing) {
        return prevFilters.map((f) => (f.id === clientId ? { ...f, ...patch } : f));
      }
      return [...prevFilters, { id: clientId, ...patch }];
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Can I='read' a='fin-report'>
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12}>
            <MDBox mt={3} mb={2} display='flex' justifyContent='space-between' alignItems='center'>
              <MDBox display='flex' alignItems='center' gap={1.5}>
                <MDBox
                  sx={{
                    width: 42, height: 42, borderRadius: '10px',
                    background: '#81c78418', border: '1px solid #81c78440',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}
                >
                  <Icon sx={{ fontSize: '1.4rem', color: '#81c784' }}>account_balance</Icon>
                </MDBox>
                <MDTypography variant='h5' fontWeight='bold'>
                  Financial Report
                </MDTypography>
              </MDBox>
              <MDButton variant='gradient' color='info' onClick={() => handleExport()}>
                Export All
              </MDButton>
            </MDBox>
          </Grid>
          {data.map((item) => (
            <Grid item xs={12} id={item?.clientId} key={item?.clientId}>
              <Card>
                <MDBox
                  sx={{
                    height: '3px',
                    background: 'linear-gradient(90deg, #e2001b, #ff4f69, transparent 80%)',
                    borderRadius: '12px 12px 0 0'
                  }}
                />
                <MDBox position='relative' borderRadius='lg' p={3}>
                  <MDBox mb={1}>
                    <MDBox display='flex' alignItems='center' gap={1.5} mb={2}>
                      <MDBox
                        sx={{
                          width: 36, height: 36, borderRadius: '9px',
                          background: '#4fc3f718', border: '1px solid #4fc3f740',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}
                      >
                        <Icon sx={{ fontSize: '1.1rem', color: '#4fc3f7' }}>business</Icon>
                      </MDBox>
                      <MDTypography variant='h5' fontWeight='bold' textTransform='capitalize' fontSize='1.2rem'>
                        {item?.clientName}
                      </MDTypography>
                    </MDBox>
                    <MDBox sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }} mb={2}>
                      <MDBox sx={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DateTimePicker
                            label='From'
                            ampmInClock={false}
                            closeOnSelect={true}
                            ampm={false}
                            showDaysOutsideCurrentMonth
                            value={
                              filters?.find((f) => f.id === item?.clientId)?.startDate
                                ? dayjs(filters.find((f) => f.id === item.clientId).startDate)
                                : null
                            }
                            onChange={(value) => {
                              handleFromChange(value, item?.clientId);
                            }}
                            viewRenderers={{
                              hours: null,
                              minutes: null,
                              seconds: null
                            }}
                            slotProps={{
                              layout: {
                                sx: {
                                  [`.${pickersLayoutClasses.actionBar}`]: {
                                    display: 'none'
                                  }
                                }
                              }
                            }}
                          />
                          <DateTimePicker
                            label='To'
                            ampmInClock={false}
                            closeOnSelect={true}
                            ampm={false}
                            showDaysOutsideCurrentMonth
                            value={
                              filters?.find((f) => f.id === item?.clientId)?.endDate
                                ? dayjs(filters.find((f) => f.id === item.clientId).endDate)
                                : null
                            }
                            onChange={(value) => {
                              handleToChange(value, item?.clientId);
                            }}
                            viewRenderers={{
                              hours: null,
                              minutes: null,
                              seconds: null
                            }}
                            slotProps={{
                              layout: {
                                sx: {
                                  [`.${pickersLayoutClasses.actionBar}`]: {
                                    display: 'none'
                                  }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </MDBox>
                      <MDBox display='flex' gap='80px'>
                        <Autocomplete
                          options={[...Currency, { label: 'Overall', id: 0 }]}
                          defaultValue={{
                            label: 'Overall',
                            id: 0
                          }}
                          value={{
                            label: filters?.find((f) => f.id === item?.clientId)?.currency || 'Overall',
                            id: filters?.find((f) => f.id === item?.clientId)?.currency || 0
                          }}
                          sx={{ width: '200px' }}
                          autoSelect
                          autoFocus
                          getOptionLabel={(option) => option.label}
                          onChange={(e, value) => {
                            updateFilter(item?.clientId, { currency: value === null ? 0 : value?.id });
                          }}
                          renderInput={(params) => <TextField {...params} label='Currency' />}
                        />
                        <MDButton variant='gradient' color='info' onClick={() => handleExport(item?.clientId)}>
                          Export
                        </MDButton>
                      </MDBox>
                    </MDBox>
                    <MDTypography fontSize='13px'>
                      {filters?.find((f) => f.id === item?.clientId)?.startDate ||
                        filters?.find((f) => f.id === item?.clientId)?.endDate
                        ? `Showing results from ${filters?.find((f) => f.id === item?.clientId)?.startDate
                          ? new Date(filters?.find((f) => f.id === item?.clientId)?.startDate).toLocaleDateString()
                          : 'the beggining'
                        } to ${filters?.find((f) => f.id === item?.clientId)?.endDate
                          ? new Date(filters?.find((f) => f.id === item?.clientId)?.endDate).toLocaleDateString()
                          : 'now'
                        }`
                        : 'Showing results for all the time'}{' '}
                    </MDTypography>
                  </MDBox>
                  <MDBox display='flex' justifyContent='space-between' alignItems='center' flexDirection='column'>
                    {item?.metrics?.map((metric, i) => (
                      <Grid
                        container
                        spacing={2}
                        pb={3}
                        m={3}
                        id={`${item.clientId}_${i}`}
                        key={`${item.clientId}_${i}`}
                        pt={3}
                        border={'1px solid #E0E0E0'}
                        borderRadius='8px'
                      >
                        <Grid item xs={12} style={{ paddintTop: '0px !important' }}>
                          <MDTypography variant='h6' fontWeight='bold' fontSize='1.1rem'>
                            {metric?.gameName || metric?.gameType}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12} md={6} lg={4}>
                          <MDTypography
                            variant='text'
                            fontWeight='medium'
                            fontSize='1rem'
                            style={{ marginRight: '8px' }}
                          >
                            Total bets:
                          </MDTypography>
                          <MDTypography variant='text' fontSize='1rem'>
                            {metric?.totalBets}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12} md={6} lg={4}>
                          <MDTypography
                            variant='text'
                            fontWeight='medium'
                            fontSize='1rem'
                            style={{ marginRight: '8px' }}
                          >
                            Total rake:
                          </MDTypography>
                          <MDTypography variant='text' fontSize='1rem'>
                            {metric?.totalRake} {filters?.find((f) => f.id === item?.clientId)?.currency || 'USD'}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12} md={6} lg={4}>
                          <MDTypography
                            variant='text'
                            fontWeight='medium'
                            fontSize='1rem'
                            style={{ marginRight: '8px' }}
                          >
                            Total wagered:
                          </MDTypography>
                          <MDTypography variant='text' fontSize='1rem'>
                            {metric?.totalWagered} {filters?.find((f) => f.id === item?.clientId)?.currency || 'USD'}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12} md={6} lg={4}>
                          <MDTypography
                            variant='text'
                            fontWeight='medium'
                            fontSize='1rem'
                            style={{ marginRight: '8px' }}
                          >
                            Average bet:
                          </MDTypography>
                          <MDTypography variant='text' fontSize='1rem'>
                            {metric?.averageBet} {filters?.find((f) => f.id === item?.clientId)?.currency || 'USD'}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12} md={6} lg={4}>
                          <MDTypography
                            variant='text'
                            fontWeight='medium'
                            fontSize='1rem'
                            style={{ marginRight: '8px' }}
                          >
                            Non rollback bets:
                          </MDTypography>
                          <MDTypography variant='text' fontSize='1rem'>
                            {metric?.nonRollbackBets}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={12} md={6} lg={4}>
                          <MDTypography
                            variant='text'
                            fontWeight='medium'
                            fontSize='1rem'
                            style={{ marginRight: '8px' }}
                          >
                            Winner count:
                          </MDTypography>

                          <MDTypography variant='text' fontSize='1rem'>
                            {metric?.winnerCount}
                          </MDTypography>
                        </Grid>
                      </Grid>
                    ))}
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Can>
      <Can not I='read' a='fin-report'>
        <Navigate to='/dashboard' replace />
      </Can>
    </DashboardLayout>
  );
}

export default FinancialReport;
